import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitAvatar, KitFormBar, KitKV } from '../common/MobileKitExtras';
import { useAppBarAction, useAppBarTitle } from '../layout/AppBarActions';
import { initials, vehicleLabel } from '../serviceorder/orderMobileUtils';
import { quoteStatusView, quoteValidUntil } from './quoteMobileUtils';

/**
 * Detalhe do orçamento (somente leitura) no shell mobile — `prototipos-mobile/orcamentos/detalhe.html`.
 * Só apresentação: dados, permissões e ações vêm de `pages/service_quotes/View.js`.
 */

const WrenchIcon = protoIcon('wrench');
const PackageIcon = protoIcon('package');
const PrintIcon = protoIcon('print');
const MsgIcon = protoIcon('msg');
const LinkIcon = protoIcon('link');
const ShareIcon = protoIcon('share');
const DotsIcon = protoIcon('doth');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

// `.sheet .action-list .item`
const ACTION_ITEM_SX = { border: 0, borderBottom: 0, p: '12px 4px' };

const typeOf = (item) => item.type_item || item.type;

export default function MobileQuoteDetail({
    loading,
    quoteNumber,
    status,
    details,
    usesVehicle,
    items,
    loadingItems,
    total,
    canEdit,
    canDelete,
    canConvert,
    canSendWhatsapp,
    whatsappConnected,
    converted,
    printing,
    onPrint,
    onEdit,
    onDelete,
    onConvert,
    onOperate,
    onCopyLink,
    onWhatsapp,
}) {
    const [actionsOpen, setActionsOpen] = useState(false);
    const view = quoteStatusView(status);

    useAppBarTitle(!loading && quoteNumber ? `Orçamento #${quoteNumber}` : '');
    // Mesmo padrão da OS: Imprimir direto no AppBar + "Ações" (⋯) abrindo a folha.
    useAppBarAction({
        actions: [
            { label: 'Imprimir', icon: PrintIcon, onClick: onPrint, disabled: loading || printing },
            { label: 'Ações', icon: DotsIcon, onClick: () => setActionsOpen(true), disabled: loading },
        ],
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const valid = quoteValidUntil(details.quote_date);
    const showPlate = details.vehicle_plate && details.vehicle_plate !== '-' && details.vehicle_type !== 'bicycle';
    const vehicle = usesVehicle ? vehicleLabel(details.vehicle_name) : '';
    const subtitle = vehicle ? `${vehicle}${showPlate ? ` · ${details.vehicle_plate}` : ''}` : (details.customer_contact || '');
    const sorted = [...items].sort((a, b) => Number(typeOf(b) === 'service') - Number(typeOf(a) === 'service'));
    const hasBar = !converted;
    const canDeleteQuote = canDelete && !converted;

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <KitPill tone={view.tone}>{view.label}</KitPill>
                    {valid && <Box component="span" sx={{ fontSize: 12.5, color: 'text.disabled' }}>Válido até {valid.format('DD/MM')}</Box>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '12px' }}>
                    <KitAvatar text={initials(details.customer_name)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{details.customer_name || '—'}</Box>
                        {subtitle && <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>{subtitle}</Box>}
                    </Box>
                </Box>
            </KitCard>

            {details.defect_reason && (
                <>
                    <SecH title="Motivo / defeito" />
                    <KitCard>{details.defect_reason}</KitCard>
                </>
            )}

            {details.observations && (
                <>
                    <SecH title="Observações" />
                    <KitCard>{details.observations}</KitCard>
                </>
            )}

            <SecH title="Itens" />
            {loadingItems ? (
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
            ) : (
                <KitList>
                    {sorted.length === 0 && <KitItem icon={PackageIcon} tone="gray" title="Nenhum item neste orçamento" sub="Este orçamento não possui itens registrados" />}
                    {sorted.map((item) => {
                        const isService = typeOf(item) === 'service';
                        return (
                            <KitItem
                                key={item.id ?? item.item_id}
                                icon={isService ? WrenchIcon : PackageIcon}
                                title={item.item_name || item.name}
                                sub={isService ? '' : `${item.quantity} un`}
                                end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {item.amount_total ?? item.total ?? '0,00'}</Box>}
                            />
                        );
                    })}
                </KitList>
            )}

            <KitCard sx={{ mt: '12px' }}>
                <KitKV big label="Total" value={`R$ ${total}`} sx={{ border: 0, m: 0, p: 0 }} />
            </KitCard>

            <SecH title="Compartilhar" />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <KitButton
                    sx={{ backgroundColor: 'success.main', color: '#fff', border: 0, ...((!canSendWhatsapp || !whatsappConnected) ? { opacity: 0.5 } : {}) }}
                    disabled={!canSendWhatsapp || !whatsappConnected}
                    onClick={onWhatsapp}
                >
                    <MsgIcon size={18} /> WhatsApp
                </KitButton>
                <KitButton onClick={onCopyLink}><LinkIcon size={18} /> Link público</KitButton>
            </Box>

            {hasBar && (
                <KitFormBar>
                    {canConvert && (
                        <KitButton onClick={onOperate}><WrenchIcon size={18} /> Operar</KitButton>
                    )}
                    {canConvert ? (
                        <KitButton variant="primary" onClick={onConvert}>Gerar Ordem</KitButton>
                    ) : (
                        <KitButton variant="primary" onClick={onOperate}><WrenchIcon size={18} /> Operar</KitButton>
                    )}
                </KitFormBar>
            )}

            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    <KitItem
                        icon={ShareIcon}
                        title="Enviar link ao cliente"
                        sub="Link público do orçamento"
                        chevron
                        sx={ACTION_ITEM_SX}
                        onClick={() => { setActionsOpen(false); onCopyLink(); }}
                    />
                    {canSendWhatsapp && (
                        <KitItem
                            icon={MsgIcon}
                            title="Enviar por WhatsApp"
                            sub={whatsappConnected ? undefined : 'WhatsApp não conectado'}
                            chevron
                            sx={{ ...ACTION_ITEM_SX, ...(whatsappConnected ? {} : { opacity: 0.5 }) }}
                            onClick={() => { if (!whatsappConnected) return; setActionsOpen(false); onWhatsapp(); }}
                        />
                    )}
                    {canEdit && (
                        <KitItem icon={EditIcon} title="Editar dados" chevron sx={ACTION_ITEM_SX} onClick={() => { setActionsOpen(false); onEdit(); }} />
                    )}
                    {canDeleteQuote && (
                        <KitItem icon={TrashIcon} tone="red" title="Excluir orçamento" chevron sx={ACTION_ITEM_SX} onClick={() => { setActionsOpen(false); onDelete(); }} />
                    )}
                </Box>
            </MobileSheet>
        </Box>
    );
}
